import { alpha, Theme, Components } from '@mui/material/styles';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { toggleButtonClasses } from '@mui/material/ToggleButton';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { gray, brand } from '../themePrimitives';

/* eslint-disable import/prefer-default-export */
export const inputsCustomizations: Components<Theme> = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        boxSizing: 'border-box',
        transition: 'all 100ms ease-in',
        '&:focus-visible': {
          outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          outlineOffset: '2px',
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        textTransform: 'none',
        boxShadow: 'none',
        backgroundColor: '#0d0d0c',
        color: '#d0d1c5',
        border: '1px solid #d0d1c5',
        transition: 'background-color 80ms ease, color 80ms ease',
        '&:hover': {
          backgroundColor: '#0d0d0c',
          boxShadow: 'none',
        },
        '&:active': {
          backgroundColor: '#d0d1c5',
          color: '#0d0d0c',
          boxShadow: 'none',
        },
        variants: [
          {
            props: { size: 'small' },
            style: { height: '2.25rem', padding: '8px 12px' },
          },
          {
            props: { size: 'medium' },
            style: { height: '2.5rem' },
          },
        ],
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: 'none',
        borderRadius: 0,
        color: (theme.vars || theme).palette.text.primary,
        border: 'none',
        backgroundColor: 'transparent',
        transition: 'background-color 80ms ease, color 80ms ease',
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&:active': {
          color: '#d0d1c5',
        },
        variants: [
          {
            props: { size: 'small' },
            style: {
              width: '2.25rem',
              height: '2.25rem',
              padding: '0.25rem',
              [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
            },
          },
          {
            props: { size: 'medium' },
            style: { width: '2.5rem', height: '2.5rem' },
          },
        ],
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '10px',
        boxShadow: `0 4px 16px ${alpha(gray[400], 0.2)}`,
        [`& .${toggleButtonGroupClasses.selected}`]: {
          color: brand[500],
        },
        ...theme.applyStyles('dark', {
          [`& .${toggleButtonGroupClasses.selected}`]: {
            color: '#fff',
          },
          boxShadow: `0 4px 16px ${alpha(brand[700], 0.5)}`,
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '12px 16px',
        textTransform: 'none',
        borderRadius: '10px',
        fontWeight: 500,
        ...theme.applyStyles('dark', {
          color: gray[400],
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
          [`&.${toggleButtonClasses.selected}`]: {
            color: brand[300],
          },
        }),
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: (
        <CheckBoxOutlineBlankRoundedIcon sx={{ color: 'hsla(210, 0%, 0%, 0.0)' }} />
      ),
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,
        borderRadius: 0,
        border: '1px solid #d0d1c5',
        boxShadow: 'none',
        backgroundColor: 'transparent',
        transition: 'border-color, background-color, 120ms ease-in',
        '&:hover': {
          borderColor: '#d0d1c5',
        },
        '&.Mui-checked': {
          color: '#0d0d0c',
          backgroundColor: '#d0d1c5',
          borderColor: '#d0d1c5',
          boxShadow: 'none',
        },
        ...theme.applyStyles('dark', {
          borderColor: '#d0d1c5',
          backgroundColor: 'transparent',
        }),
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        border: 'none',
      },
      input: {
        '&::placeholder': {
          opacity: 0.7,
          color: gray[500],
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: 0,
      },
      root: ({ theme }) => ({
        padding: '8px 12px',
        color: (theme.vars || theme).palette.text.primary,
        borderRadius: 0,
        border: `1px solid #d0d1c5`,
        backgroundColor: '#0d0d0c',
        transition: 'border 120ms ease-in',
        '&:hover': {
          borderColor: '#d0d1c5',
        },
        [`&.${outlinedInputClasses.focused}`]: {
          outline: `3px solid ${alpha('#d0d1c5', 0.3)}`,
          borderColor: '#d0d1c5',
        },
        variants: [
          {
            props: { size: 'small' },
            style: { height: '2.25rem' },
          },
          {
            props: { size: 'medium' },
            style: { height: '2.5rem' },
          },
        ],
      }),
      notchedOutline: {
        border: 'none',
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: (theme.vars || theme).palette.grey[500],
        ...theme.applyStyles('dark', {
          color: (theme.vars || theme).palette.grey[400],
        }),
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        typography: theme.typography.caption,
        marginBottom: 8,
        color: '#d0d1c5',
      }),
    },
  },
};
